import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { PageHeader } from '../components/ui/PageHeader';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/Toast';

interface ExpectedDelivery {
  supplierId: string;
  supplierName: string;
  cutoffTime: string | null;
  poCount: number;
  earliestExpectedAt: string | null;
  pendingUnits: number;
  purchaseOrders: Array<{
    id: string;
    poNumber: string;
    status: string;
    supplierOrderRef: string | null;
    expectedAt: string | null;
  }>;
}

interface OrderToPrepare {
  orderId: string;
  orderNumber: string;
  status: string;
  customerName: string | null;
  /** The seller's name, or "the customer (garage portal)" for a portal order. */
  orderedBy: string | null;
  channel: string;
  /** The bin the order's parts go into: its own, or the garage's live bin (one per garage). */
  binLocation: string | null;
  openLines: number;
  awaitingLines: number;
  /** 3.12 — awaiting lines that are on NO supplier order: nothing is coming. */
  notOrderedLines: number;
  pickedLines: number;
  /** COUNTER_SALE: sold at the till, the buyer is waiting — prepared first. */
  bucket: 'COUNTER_SALE' | 'READY_TO_PREPARE' | 'AWAITING_SUPPLIER';
  awaitingSuppliers: Array<{ supplierName: string; expectedAt: string | null }>;
}

interface PartialBasket {
  orderId: string;
  orderNumber: string;
  customerName: string | null;
  remainingLines: number;
}

interface ReadyBasket {
  orderId: string;
  orderNumber: string;
  customerName: string | null;
  slipNumber: string | null;
  pickupCode: string | null;
  deadlineAt: string | null;
  deliveryNoteNumber: string | null;
}

interface Dashboard {
  expectedDeliveries: ExpectedDelivery[];
  ordersToPrepare: OrderToPrepare[];
  partialBaskets: PartialBasket[];
  readyBaskets: ReadyBasket[];
  quarantineCount: number;
  productsToComplete: number;
}

const time = (iso: string | null): string =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

/**
 * SC-02 "Day D+1 morning" — the WAREHOUSE tablet dashboard, all four sections.
 *
 * Tablet-first: this is the screen the warehouse opens at 8am on a device it operates with
 * gloves on, so rows are large tap targets and every section links straight to the work rather
 * than to a filter the operator then has to set. One request feeds all four panels — four
 * sequential fetches over warehouse wifi is what makes a tablet feel broken.
 */
export default function WarehouseDashboard() {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const [data, setData] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async (): Promise<void> => {
    try {
      const res = await apiClient.get<Dashboard>('/warehouse/dashboard');
      setData(res.data);
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const counter = data?.ordersToPrepare.filter((o) => o.bucket === 'COUNTER_SALE') ?? [];
  const ready = data?.ordersToPrepare.filter((o) => o.bucket === 'READY_TO_PREPARE') ?? [];
  const awaiting = data?.ordersToPrepare.filter((o) => o.bucket === 'AWAITING_SUPPLIER') ?? [];

  return (
    <div>
      <PageHeader
        title={t('warehouseDashboard.title')}
        description={t('warehouseDashboard.description')}
        actions={
          <Button variant="outline" onClick={() => void load()} disabled={loading}>
            {t('common.refresh')}
          </Button>
        }
      />

      {data?.quarantineCount || data?.productsToComplete ? (
        <div className="mb-4 flex flex-wrap gap-2">
          {data.quarantineCount > 0 && (
            <Link to="/warehouse/quarantine">
              <Badge variant="warning" className="cursor-pointer px-3 py-1 text-sm">
                {t('warehouseDashboard.quarantineCount', { count: data.quarantineCount })}
              </Badge>
            </Link>
          )}
          {data.productsToComplete > 0 && (
            <Link to="/products?draft=1">
              <Badge variant="secondary" className="cursor-pointer px-3 py-1 text-sm">
                {t('warehouseDashboard.productsToComplete', { count: data.productsToComplete })}
              </Badge>
            </Link>
          )}
        </div>
      ) : null}

      {loading ? (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          {/* 1 — EXPECTED DELIVERIES */}
          <Section
            title={t('warehouseDashboard.expectedDeliveries')}
            count={data?.expectedDeliveries.length ?? 0}
          >
            {(data?.expectedDeliveries ?? []).map((s) => (
              <Row key={s.supplierId}>
                <div className="min-w-0">
                  <div className="truncate font-medium">{s.supplierName}</div>
                  <div className="text-xs text-muted-foreground">
                    {t('warehouseDashboard.poCount', { count: s.poCount })}
                    {' · '}
                    {t('warehouseDashboard.pendingUnits', { count: s.pendingUnits })}
                    {s.cutoffTime &&
                      ` · ${t('warehouseDashboard.cutoff', { time: s.cutoffTime.slice(0, 5) })}`}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-xs text-muted-foreground">{time(s.earliestExpectedAt)}</div>
                  <Link
                    className="text-xs underline"
                    to={`/warehouse/reception?ref=${encodeURIComponent(s.purchaseOrders[0]?.supplierOrderRef ?? s.purchaseOrders[0]?.poNumber ?? '')}`}
                  >
                    {t('warehouseDashboard.receive')}
                  </Link>
                </div>
              </Row>
            ))}
            <Empty
              show={(data?.expectedDeliveries.length ?? 0) === 0}
              label={t('warehouseDashboard.noDeliveries')}
            />
          </Section>

          {/* 2 — ORDERS TO PREPARE, in the guide's two colour buckets */}
          <Section
            title={t('warehouseDashboard.ordersToPrepare')}
            count={data?.ordersToPrepare.length ?? 0}
          >
            {counter.length > 0 && (
              <div className="px-3 pt-1 text-xs font-semibold uppercase text-destructive">
                {t('warehouseDashboard.counterSales')}
              </div>
            )}
            {counter.map((o) => (
              <Row key={o.orderId}>
                <Link to={`/orders/${o.orderId}`} className="min-w-0 hover:underline">
                  <div className="truncate font-medium">{o.orderNumber}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {t('warehouseDashboard.soldBy', { name: o.orderedBy ?? '—' })}
                    {o.customerName ? ` · ${o.customerName}` : ''}
                  </div>
                </Link>
                <Link to={`/warehouse/picking?orderId=${o.orderId}`} className="shrink-0">
                  {/* Everything picked: the parts go to the seller, who hands them over. */}
                  {o.pickedLines > 0 && o.pickedLines >= o.openLines - o.awaitingLines ? (
                    <Badge variant="secondary">{t('warehouseDashboard.toCounter')}</Badge>
                  ) : (
                    <Badge variant="destructive">
                      {t('warehouseDashboard.pick', { count: o.openLines - o.awaitingLines })}
                    </Badge>
                  )}
                </Link>
              </Row>
            ))}
            {ready.length > 0 && (
              <div className="px-3 pt-1 text-xs font-semibold uppercase text-success">
                {t('warehouseDashboard.readyToPrepare')}
              </div>
            )}
            {ready.map((o) => (
              <Row key={o.orderId}>
                <Link to={`/orders/${o.orderId}`} className="min-w-0 hover:underline">
                  <div className="truncate font-medium">{o.orderNumber}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {o.customerName ?? '—'}
                    {o.channel === 'PORTAL' && ` · ${t('warehouseDashboard.portal')}`}
                    {o.binLocation && ` · ${t('warehouseDashboard.bin', { bin: o.binLocation })}`}
                  </div>
                </Link>
                <Link to={`/warehouse/picking?orderId=${o.orderId}`} className="shrink-0">
                  {/* What is still on the shelf; everything scanned is in the bin (1.1). */}
                  {o.openLines - o.pickedLines > 0 ? (
                    <Badge variant="success">
                      {t('warehouseDashboard.pick', { count: o.openLines - o.pickedLines })}
                    </Badge>
                  ) : (
                    <Badge variant="secondary">
                      {t('warehouseDashboard.inBin', { bin: o.binLocation ?? '—' })}
                    </Badge>
                  )}
                </Link>
              </Row>
            ))}
            {awaiting.length > 0 && (
              <div className="px-3 pt-2 text-xs font-semibold uppercase text-warning">
                {t('warehouseDashboard.awaitingSupplier')}
              </div>
            )}
            {awaiting.map((o) => (
              <Row key={o.orderId}>
                <Link to={`/orders/${o.orderId}`} className="min-w-0 hover:underline">
                  <div className="truncate font-medium">{o.orderNumber}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {o.awaitingSuppliers
                      .map((s) => `${s.supplierName} · ${time(s.expectedAt)}`)
                      .join(' — ') ||
                      (o.customerName ?? '—')}
                  </div>
                </Link>
                <div className="flex shrink-0 items-center gap-1">
                  {/* 3.12 — the board must not read "expected" for a part nobody ordered. */}
                  {o.notOrderedLines > 0 && (
                    <Badge variant="destructive">
                      {t('warehouseDashboard.notOrdered', { count: o.notOrderedLines })}
                    </Badge>
                  )}
                  <Badge variant="warning">{o.awaitingLines}</Badge>
                </div>
              </Row>
            ))}
            <Empty
              show={(data?.ordersToPrepare.length ?? 0) === 0}
              label={t('warehouseDashboard.noOrders')}
            />
          </Section>

          {/* 3 — PARTIAL BASKETS */}
          <Section
            title={t('warehouseDashboard.partialBaskets')}
            count={data?.partialBaskets.length ?? 0}
          >
            {(data?.partialBaskets ?? []).map((o) => (
              <Row key={o.orderId}>
                <Link to={`/orders/${o.orderId}`} className="min-w-0 hover:underline">
                  <div className="truncate font-medium">{o.orderNumber}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {o.customerName ?? '—'}
                  </div>
                </Link>
                <Badge variant="secondary" className="shrink-0">
                  {t('warehouseDashboard.remainingLines', { count: o.remainingLines })}
                </Badge>
              </Row>
            ))}
            <Empty
              show={(data?.partialBaskets.length ?? 0) === 0}
              label={t('warehouseDashboard.noPartials')}
            />
          </Section>

          {/* 4 — READY BASKETS (Zone E), with the collection code the counter scans */}
          <Section
            title={t('warehouseDashboard.readyBaskets')}
            count={data?.readyBaskets.length ?? 0}
          >
            {(data?.readyBaskets ?? []).map((o) => (
              <Row key={o.orderId}>
                <Link to={`/orders/${o.orderId}`} className="min-w-0 hover:underline">
                  <div className="truncate font-medium">{o.orderNumber}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {o.customerName ?? '—'}
                  </div>
                </Link>
                <div className="shrink-0 text-right">
                  {o.pickupCode && (
                    <div className="font-mono text-sm font-semibold tracking-widest">
                      {o.pickupCode}
                    </div>
                  )}
                  {o.deadlineAt && (
                    <div className="text-xs text-muted-foreground">
                      {t('warehouseDashboard.until', { date: time(o.deadlineAt) })}
                    </div>
                  )}
                </div>
              </Row>
            ))}
            <Empty
              show={(data?.readyBaskets.length ?? 0) === 0}
              label={t('warehouseDashboard.noReady')}
            />
          </Section>
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border bg-card shadow-sm">
      <header className="flex items-center justify-between border-b px-4 py-3">
        <h2 className="text-base font-semibold">{title}</h2>
        <Badge variant="muted">{count}</Badge>
      </header>
      <div className="max-h-96 divide-y overflow-y-auto">{children}</div>
    </section>
  );
}

/** Deliberately roomy: this is operated on a tablet, standing up, often with gloves. */
function Row({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 text-sm">{children}</div>
  );
}

function Empty({ show, label }: { show: boolean; label: string }) {
  if (!show) return null;
  return <div className="px-4 py-6 text-center text-sm text-muted-foreground">{label}</div>;
}
